import type { FeatureApiResponse } from "@growthbook/growthbook";

import { useWindowSize } from "@vueuse/core";
import { defineNuxtPlugin, useNuxtApp, useRouter, useState } from "#app";
import { useRoute } from "#app/composables/router";
import { useRequestURL } from "#app/composables/url";
import { computed } from "vue";

import {
  createAbTestingClient,
  linkMapFeature,
  runFeatures,
  splitUrlFeature,
} from "@solvari/ab-testing";
import { resolveClientId, toOriginPath } from "@solvari/utils";

import { siteAttributes } from "~/shared/abTesting/attributes.ts";
import { useHost } from "~/shared/composables/useHost.ts";
import { abTestingRenderPayloadStateKey } from "~/shared/libs/abTestingContext.ts";

export default defineNuxtPlugin(async () => {
  const router = useRouter();
  const route = useRoute();
  const { origin, host } = useRequestURL();
  const siteHost = useHost();
  const screenWidth = useWindowSize().width;

  const clientId = resolveClientId();
  if (clientId === null) {
    return;
  }

  const nuxtApp = useNuxtApp();
  const hydrated = new Promise<void>((resolve) => {
    nuxtApp.hooks.hookOnce("app:suspense:resolve", () => resolve());
  });
  // After Nuxt's own `app:created` replace — an earlier `router.replace` gets overwritten.
  const navigationReady = new Promise<void>((resolve) => {
    nuxtApp.hooks.hookOnce("app:mounted", () => resolve());
  });

  const payload =
    useState<FeatureApiResponse | null>(abTestingRenderPayloadStateKey).value ??
    undefined;

  const attrs = computed(() =>
    siteAttributes({ host: siteHost, pathname: route.path }),
  );

  const instance = await createAbTestingClient({
    browserAttributes: {
      screenWidth,
      url: computed(() => toOriginPath(route.path, origin) ?? ""),
      urlDomain: host,
      urlFull: computed(() => `${origin}${route.fullPath}`),
      urlPath: computed(() => route.path),
    },
    clientId,
    payload,
    siteAttributes: {
      siteLocale: computed(() => attrs.value.siteLocale),
      siteType: computed(() => attrs.value.siteType),
    },
  });

  // Mount never waits on the CDN — features gate themselves on `whenAbTestingLoaded()`.
  void runFeatures(
    instance,
    [() => splitUrlFeature({ router }), () => linkMapFeature()],
    { domReady: hydrated, navigationReady },
  );
});
